import { useState } from 'react';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { AIConfigSettings } from '@/components/admin/AIConfigSettings';
import { APIKeyManager } from '@/components/admin/APIKeyManager';
import { ThemeSettings } from '@/components/admin/ThemeSettings';
import { BrandingSettings } from '@/components/admin/BrandingSettings';
import { RAGDocumentManager } from '@/components/admin/RAGDocumentManager';
import { UserManagement } from '@/components/admin/UserManagement';
import { DatabaseExplorer } from '@/components/admin/DatabaseExplorer';
import { AnalyticsDashboard } from '@/components/admin/AnalyticsDashboard';
import { UserCog, Bot, KeyRound, Palette, Sparkles, BookOpen, Database, BarChart3, Users } from '@/lib/category-icons';
import { AgentManagement } from '@/components/admin/AgentManagement';
import type { LucideIcon } from 'lucide-react';

type AdminTab = 'users' | 'ai' | 'api-keys' | 'theme' | 'branding' | 'rag' | 'database' | 'analytics' | 'agents';

export function AdminPage() {
  const { isAdmin, isSuperAdmin, loading: authLoading } = useAdminAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="glass-strong p-8 rounded-2xl text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">
            You don't have permission to access the admin panel. Please contact a super admin to request access.
          </p>
        </div>
      </div>
    );
  }

  const tabs: Array<{ id: AdminTab; label: string; icon: LucideIcon; requireSuperAdmin?: boolean }> = [
    { id: 'users', label: 'Users', icon: UserCog },
    { id: 'ai', label: 'AI Configuration', icon: Bot },
    { id: 'agents', label: 'Coaching Agents', icon: Users },
    { id: 'api-keys', label: 'API Keys', icon: KeyRound, requireSuperAdmin: true },
    { id: 'theme', label: 'Theme', icon: Palette },
    { id: 'branding', label: 'Branding', icon: Sparkles },
    { id: 'rag', label: 'Documents', icon: BookOpen },
    { id: 'database', label: 'Database', icon: Database, requireSuperAdmin: true },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  const visibleTabs = tabs.filter(tab => !tab.requireSuperAdmin || isSuperAdmin);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="glass-strong border-b border-charcoal-700/50 p-6">
        <h1 className="text-2xl font-bold text-white mb-2">Admin Dashboard</h1>
        <p className="text-slate-400 text-sm">
          Manage application settings, AI configuration, and branding
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="glass border-b border-charcoal-700/50 px-6 pt-4">
        <div className="flex gap-2 -mb-px">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-t-lg text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-charcoal-800 text-white border-b-2 border-amber-500'
                  : 'text-slate-400 hover:text-white hover:bg-charcoal-800/50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className={`${activeTab === 'database' ? 'w-full' : 'max-w-5xl'} mx-auto`}>
          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'ai' && <AIConfigSettings isSuperAdmin={isSuperAdmin} />}
          {activeTab === 'agents' && <AgentManagement />}
          {activeTab === 'api-keys' && isSuperAdmin && <APIKeyManager />}
          {activeTab === 'theme' && <ThemeSettings />}
          {activeTab === 'branding' && <BrandingSettings />}
          {activeTab === 'rag' && <RAGDocumentManager />}
          {activeTab === 'database' && isSuperAdmin && <DatabaseExplorer />}
          {activeTab === 'analytics' && <AnalyticsDashboard />}
        </div>
      </div>
    </div>
  );
}
